/**
 * Opções dos gráficos, montadas como funções puras.
 *
 * As três regras de apresentação do projeto são impostas **aqui**, e não deixadas a
 * cargo de cada tela — regra que depende de quem desenha o gráfico é regra que alguém
 * esquece:
 *
 *   1. percentis, nunca média, como métrica de latência (o título do eixo diz qual);
 *   2. eixo de concorrência em escala logarítmica;
 *   3. linha do alvo em todo gráfico de latência.
 *
 * Ficam fora do componente para serem verificadas por teste sem desenhar nada: o
 * componente só entrega estas opções ao ECharts.
 */

import type {
  BarSeriesOption,
  EChartsOption,
  LineSeriesOption,
  XAXisComponentOption,
  YAXisComponentOption,
} from "echarts";

/** Um ponto de série temporal ou de curva, com a série a que pertence. */
export interface SeriesPoint {
  readonly x: string | number;
  readonly y: number;
  readonly series: string;
}

/** Linha de referência desenhada sobre o gráfico — o alvo, ou a saturação. */
export interface TargetLine {
  readonly value: number;
  readonly label: string;
}

/** Cores já resolvidas para o modo corrente (claro ou escuro). */
export interface ChartColors {
  readonly series: readonly string[];
  readonly target: string;
  readonly axisText: string;
  readonly gridLine: string;
}

/**
 * Tipo do eixo horizontal.
 *
 * Logarítmico é obrigatório no eixo de concorrência: em escala linear, a faixa de 1 a
 * 128 comprime justamente o joelho da curva, que é o único ponto de interesse.
 */
export type XAxisKind = "value" | "log";

/** Entradas do gráfico de linhas. */
export interface LineChartInput {
  readonly data: readonly SeriesPoint[];
  readonly xTitle: string;
  readonly yTitle: string;
  readonly xAxis: XAxisKind;
  /** Linha de referência; `null` só onde não há alvo a comparar. */
  readonly target: TargetLine | null;
  readonly colors: ChartColors;
  readonly smooth?: boolean;
  readonly showPoints?: boolean;
}

/** Uma série agrupada, com os pontos já ordenados pelo eixo horizontal. */
export interface GroupedSeries {
  readonly name: string;
  readonly points: ReadonlyArray<readonly [number, number]>;
}

/** Duração da animação de entrada dos gráficos, em milissegundos. */
export const CHART_ANIMATION_MS = 700;

/** Base do eixo logarítmico: concorrência cresce em potências de dois (1, 2, 4 … 128). */
export const LOG_AXIS_BASE = 2;

/**
 * Agrupa os pontos por série, na ordem em que cada série aparece pela primeira vez, e
 * ordena cada uma pelo eixo horizontal.
 *
 * Ponto com `x` não numérico é descartado: numa curva de eixo numérico ele não tem onde
 * ser desenhado, e deixá-lo passar faria o ECharts ligar a linha a um lugar arbitrário.
 */
export function groupSeries(data: readonly SeriesPoint[]): GroupedSeries[] {
  // `Map` itera na ordem de inserção: a primeira aparição de cada série fixa a ordem da
  // legenda, sem precisar de uma lista paralela.
  const byName = new Map<string, Array<readonly [number, number]>>();
  for (const point of data) {
    const x = Number(point.x);
    if (!Number.isFinite(x)) {
      continue;
    }
    const list = byName.get(point.series);
    if (list === undefined) {
      byName.set(point.series, [[x, point.y]]);
    } else {
      list.push([x, point.y]);
    }
  }
  return [...byName.entries()].map(([name, points]) => ({
    name,
    points: [...points].sort((a, b) => a[0] - b[0]),
  }));
}

/**
 * Cor da série na posição pedida, repetindo a paleta quando há mais séries que cores.
 *
 * Paleta vazia cai na cor do alvo, e não em `undefined`: série sem cor definida recebe a
 * cor automática do ECharts, que pode coincidir com a do alvo e confundir a leitura. Com
 * paleta vazia o resto da divisão é `NaN`, a posição não existe, e o `??` resolve.
 */
export function seriesColor(colors: ChartColors, index: number): string {
  return colors.series[index % colors.series.length] ?? colors.target;
}

/** Linha tracejada do alvo, com o rótulo na ponta direita. */
function targetMarkLine(target: TargetLine, colors: ChartColors): NonNullable<LineSeriesOption["markLine"]> {
  return {
    silent: true,
    symbol: "none",
    lineStyle: { color: colors.target, type: "dashed", width: 2 },
    label: { formatter: target.label, position: "insideEndTop", color: colors.target },
    data: [{ yAxis: target.value }],
  };
}

/** Estilo comum dos rótulos e títulos de eixo. */
function axisText(colors: ChartColors): { color: string; fontSize: number } {
  return { color: colors.axisText, fontSize: 12 };
}

/**
 * Opções do gráfico de linhas.
 *
 * A linha do alvo é pendurada na primeira série; sem nenhuma série, uma série vazia a
 * carrega — o alvo continua visível mesmo antes de o dado chegar, que é justamente
 * quando o leitor precisa saber onde a curva tem de ficar.
 */
export function lineChartOption(input: LineChartInput): EChartsOption {
  const groups = groupSeries(input.data).map((group) => ({
    name: group.name,
    // Eixo logarítmico não representa zero nem negativo; o ponto é descartado em vez de
    // derrubar o gráfico inteiro.
    points: input.xAxis === "log" ? group.points.filter(([x]) => x > 0) : group.points,
  }));

  const series: LineSeriesOption[] = groups.map((group, index) => ({
    type: "line",
    name: group.name,
    data: group.points.map(([x, y]) => [x, y]),
    smooth: input.smooth ?? false,
    showSymbol: input.showPoints ?? false,
    symbolSize: 7,
    lineStyle: { width: 2.5 },
    itemStyle: { color: seriesColor(input.colors, index) },
    emphasis: { focus: "series" },
  }));

  if (input.target !== null) {
    const markLine = targetMarkLine(input.target, input.colors);
    const first = series[0];
    if (first === undefined) {
      series.push({ type: "line", name: input.target.label, data: [], markLine });
    } else {
      first.markLine = markLine;
    }
  }

  const xAxis: XAXisComponentOption =
    input.xAxis === "log"
      ? {
          type: "log",
          logBase: LOG_AXIS_BASE,
          name: input.xTitle,
          nameLocation: "middle",
          nameGap: 30,
          nameTextStyle: axisText(input.colors),
          axisLabel: axisText(input.colors),
          splitLine: { show: false },
        }
      : {
          type: "value",
          name: input.xTitle,
          nameLocation: "middle",
          nameGap: 30,
          nameTextStyle: axisText(input.colors),
          axisLabel: axisText(input.colors),
          splitLine: { show: false },
        };

  // 🛑 O ECharts NÃO desenha `markLine` fora da faixa do eixo, e a faixa automática só
  // enxerga o dado. Com latência de 4,7 ms e alvo em 10 ms, a linha do alvo sumia da tela
  // "Ao vivo" sem erro nenhum (medido em 15/09/2026). Quando o dado fica abaixo do alvo, o
  // eixo vai até o alvo com 15% de folga para o rótulo; acima dele, a faixa automática
  // já inclui o alvo e mantém a escala arredondada.
  const targetAxisHeadroom = 1.15;
  const dataMaxY = Math.max(Number.NEGATIVE_INFINITY, ...groups.flatMap((group) => group.points.map(([, y]) => y)));
  const yAxis: YAXisComponentOption = {
    type: "value",
    name: input.yTitle,
    nameLocation: "middle",
    nameGap: 52,
    nameTextStyle: axisText(input.colors),
    axisLabel: axisText(input.colors),
    splitLine: { lineStyle: { color: input.colors.gridLine } },
    ...(input.target !== null && input.target.value >= dataMaxY
      ? { max: Math.ceil(input.target.value * targetAxisHeadroom) }
      : {}),
  };

  return {
    animationDuration: CHART_ANIMATION_MS,
    animationEasing: "cubicOut",
    grid: { left: 64, right: 28, top: 44, bottom: 48 },
    legend: { top: 0, type: "scroll", textStyle: axisText(input.colors) },
    tooltip: { trigger: "axis" },
    xAxis,
    yAxis,
    series,
  };
}

/** Uma faixa do histograma, já classificada frente ao alvo. */
export interface HistogramBar {
  readonly bin: string;
  readonly count: number;
  readonly withinTarget: boolean;
}

/** Entradas do histograma. */
export interface HistogramInput {
  readonly bars: readonly HistogramBar[];
  readonly xTitle: string;
  readonly yTitle: string;
  readonly withinLabel: string;
  readonly aboveLabel: string;
  readonly withinColor: string;
  readonly aboveColor: string;
  readonly colors: ChartColors;
}

/**
 * Opções do histograma, com as faixas coloridas conforme o alvo.
 *
 * ⚠️ Colorir a barra, e não desenhar uma linha vertical: o eixo de um histograma é
 * categórico, e uma anotação de posição sobre ele cai no lugar errado — defeito
 * observado na versão anterior do protótipo, ao verificar a tela no navegador.
 *
 * As duas classes viram duas séries empilhadas, cada faixa com valor em só uma delas,
 * para que a legenda nomeie o que cada cor significa.
 */
export function histogramOption(input: HistogramInput): EChartsOption {
  const barRadius: [number, number, number, number] = [4, 4, 0, 0];
  const within: BarSeriesOption = {
    type: "bar",
    name: input.withinLabel,
    stack: "bins",
    data: input.bars.map((bar) => (bar.withinTarget ? bar.count : 0)),
    itemStyle: { color: input.withinColor, borderRadius: barRadius },
  };
  const above: BarSeriesOption = {
    type: "bar",
    name: input.aboveLabel,
    stack: "bins",
    data: input.bars.map((bar) => (bar.withinTarget ? 0 : bar.count)),
    itemStyle: { color: input.aboveColor, borderRadius: barRadius },
  };
  return {
    animationDuration: CHART_ANIMATION_MS,
    animationEasing: "cubicOut",
    grid: { left: 56, right: 20, top: 44, bottom: 52 },
    legend: { top: 0, textStyle: axisText(input.colors) },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      data: input.bars.map((bar) => bar.bin),
      name: input.xTitle,
      nameLocation: "middle",
      nameGap: 32,
      nameTextStyle: axisText(input.colors),
      axisLabel: axisText(input.colors),
    },
    yAxis: {
      type: "value",
      name: input.yTitle,
      nameLocation: "middle",
      nameGap: 40,
      nameTextStyle: axisText(input.colors),
      axisLabel: axisText(input.colors),
      splitLine: { lineStyle: { color: input.colors.gridLine } },
    },
    series: [within, above],
  };
}
